import type {GetListResponse, ParamsOptions} from "@/src/modules/core/types";
import {meService} from "@/src/modules/core/services/me";

import {citationsApi} from "../api";
import type {
  Citation,
  CreateCitationPayload,
  PatchTowDispatchPayload,
  UpdateCitationPayload,
  UploadCitationProofPayload,
} from "../types";

class CitationsService {
  async getCitations(options: ParamsOptions): Promise<GetListResponse<Citation>> {
    const {data} = await citationsApi.search(options);
    const raw = data as {data?: unknown; citations?: unknown[]};
    const items = Array.isArray(raw.data)
      ? raw.data
      : Array.isArray(raw.citations)
        ? raw.citations
        : [];
    const meta = (data as {meta?: {total: number; from?: number; to?: number}}).meta ?? {
      total: 0,
    };
    return {
      data: items as Citation[],
      meta: {
        total: meta.total,
        from: meta.from ?? 0,
        to: meta.to ?? 0,
      },
    };
  }

  async getCitation(id: number): Promise<Citation> {
    const {data} = await citationsApi.getById(id);
    return (data as {data: Citation}).data;
  }

  async createCitation(payload: CreateCitationPayload): Promise<Citation> {
    const {data} = await citationsApi.create(payload);
    return (data as {data: Citation}).data;
  }

  async updateCitation(id: number, payload: UpdateCitationPayload): Promise<Citation> {
    const {data} = await citationsApi.update(id, payload);
    return (data as {data: Citation}).data;
  }

  async deleteCitation(id: number): Promise<void> {
    await citationsApi.delete(id);
  }

  async uploadCitationProof(
    citationId: number,
    payload: UploadCitationProofPayload,
  ): Promise<Citation> {
    const {data} = await citationsApi.uploadProofs(citationId, payload);
    return (data as {data: Citation}).data;
  }

  async patchTowDispatch(
    citationId: number,
    payload: PatchTowDispatchPayload,
  ): Promise<Citation> {
    if (citationId !== payload.citation_id) {
      throw new Error("Tow dispatch payload citation_id must match route citationId");
    }
    const {data} = await citationsApi.patchTowDispatch(citationId, payload);
    return (data as {data: Citation}).data;
  }

  getCanList(): boolean {
    return meService.getHasPermission("citation show");
  }

  getCanShow(): boolean {
    return meService.getHasPermission("citation show");
  }

  getCanCreate(): boolean {
    return meService.getHasPermission("citation store");
  }

  getCanUpdate(): boolean {
    return meService.getHasPermission("citation update");
  }

  getCanDelete(): boolean {
    return meService.getHasPermission("citation delete");
  }
}

export const citationsService = new CitationsService();
